import { ProcurementPlanSubmitErrorCategory } from '@ebalance/proto/bffuser/v1/procurement_plan_model_pb'

import type { InterpolatedMessage } from '~/models/planSubmission/interpolatedMessage'
import type { StatusBadgeVariant } from '~/models/planSubmission/statusBadge'

/**
 * OCCTO BP エラーコード対応表 (差戻し / REJECTED の受付エラー詳細)。
 *
 * mock-app `SupplyDemandBalancing/Services/SubmissionErrorCode.cs` 準拠
 * (design `design/js/submission-error-codes.js` と同一データ)。 業務データの
 * SoT は mock-app であり、 コード → 名称・説明の対応そのものが OCCTO BP 仕様に
 * 由来する固定の日本語規制文言であるため、 一般 UI 文言と異なり `t()` 経由の
 * i18n 化はしない (mock-app 自身も非 i18n。 EN 切替時は日本語のまま表示される
 * — OCCTO への提出データはそもそも日本語運用のため許容)。
 *
 * "00" (正常) は対象外 — IsOk 相当の判定は呼び出し元 (エラー行のみこのテーブルを
 * 引く) が担う。
 */
export const SUBMISSION_ERROR_CODES: Readonly<
  Record<string, { name: string; description: string }>
> = {
  '01': {
    name: '情報区分コードエラー',
    description:
      '取り決め以外の情報区分コード（BPで規定された情報区分以外が入力されている等）',
  },
  '04': {
    name: '構文規則版数エラー',
    description:
      '構文規則識別版数の不正（BPで規定された版数以外が入力されている等）',
  },
  '11': {
    name: '不正データタグ',
    description:
      '不正データタグ検出（データタグの誤り、BPで登録されていないタグを検出等）',
  },
  '15': {
    name: 'データ長超過',
    description: 'データ長が最大値を超えた',
  },
  '17': {
    name: '数値変換エラー',
    description:
      '数値変換のデータ要素の値が数値でない（X要素だが数値以外が入らないところに文字データが入っているものを含む）',
  },
  '20': {
    name: '過大メッセージ長',
    description:
      '過大メッセージ長（ファイルサイズが定められたサイズを超過している）',
  },
  '22': {
    name: '負のデータ',
    description: '負のデータあり（9型データに負のデータが入力されている）',
  },
  '33': {
    name: '不正文字コード',
    description: '不正文字コードの検出',
  },
  '36': {
    name: '日付型データ不正',
    description: 'Y型（日付型）データの不正',
  },
  '60': {
    name: '不正マルチ明細',
    description:
      '不正なマルチ明細（BPで定義されない明細番号のマルチ明細が入力されている）',
  },
  '61': {
    name: '不正繰り返し回数',
    description:
      '不正な繰り返し回数（マルチ明細の繰り返し回数がBPの規定と一致していない）',
  },
  '62': {
    name: '不正XML構造',
    description: '不正なXML構造（XMLの構造がBPで定義されたものと異なる）',
  },
  '70': {
    name: 'データ整合エラー',
    description:
      'データ整合エラー（ファイル名、メッセージグループヘッダ及びメッセージ内タグの整合）：ファイル名、メッセージグループヘッダ、メッセージ内タグのデータ内容に相違がある',
  },
  '71': {
    name: 'BPID整合エラー',
    description:
      'データ整合エラー（BPID機関、BPID版）：BPID機関コード、副機関コード、BPID版が正しくないものが記載されている',
  },
  '72': {
    name: '日時整合エラー',
    description:
      'データ整合エラー（日時）：存在しない日時が指定されている。又は、提出できないタイミングでの計画提出がされている',
  },
  '73': {
    name: '送受信者コードエラー',
    description:
      'データ整合エラー（送信者コード／受信者コードエラー）：存在しない送信者コード、受信者コードが利用されている。受信者コードが実際にデータを受信した事業者と異なっている',
  },
  '74': {
    name: '連系線コードエラー',
    description:
      'データ整合エラー（連系線等コード誤り）：提出先送配電事業者の連系点コードが含まれない、又は事業者コードが異なっている',
  },
  '75': {
    name: '共通コード表エラー',
    description:
      'データ整合エラー（共通コード表以外の値が入力）：共通コード表に定められた以外の値が入力されている（70〜74でエラーとされるものを除く）',
  },
  '78': {
    name: '範囲超過エラー',
    description:
      'データ整合エラー（範囲超過）：数値が入力可能な範囲を超えて入力されている',
  },
  '79': {
    name: 'データ整合エラー(他)',
    description:
      'データ整合エラー（その他）：70〜78以外でデータの整合性にエラーがある場合',
  },
  '80': {
    name: 'ファイル間整合エラー',
    description: 'ファイル間整合エラー',
  },
  '90': {
    name: '受付不可',
    description: '受付不可能なBPの提出（受付期間外、契約未締結等）',
  },
  '91': {
    name: '必須項目未入力',
    description:
      '必須データ項目未入力：BP上入力必須とされたデータ項目が未入力の場合',
  },
  '96': {
    name: 'ファイル空',
    description: 'ファイルの中身が存在しない（ファイルサイズ0）',
  },
  '97': {
    name: 'ファイル名エラー',
    description: 'ファイル名エラー（XMLのファイル名がBPの規格で解釈できない）',
  },
  '98': {
    name: 'XML文法エラー',
    description:
      'XMLの文法エラー（XMLの文法エラーによりファイルが解釈できない）',
  },
  '99': {
    name: 'その他エラー',
    description: 'その他エラー',
  },
}

/**
 * mock `SubmissionErrorCode.IsOk` 準拠の正常判定 (review fix #3):
 * `string.IsNullOrEmpty(code) || code == "00"` = 正常。 正常コードは受付エラー
 * 詳細の表示対象から除外する (backend の verbatim 永続化はそのまま — 監査証跡
 * としての生データ保持と、 表示対象の絞り込みは別関心)。
 */
function isOkReceptionErrorCode(code: string): boolean {
  return code === '' || code === '00'
}

/**
 * 差戻し (REJECTED) の 1 件の受付エラー詳細を、 mock `SubmissionErrorCode.GetTooltip`
 * 形式 (`[code] 名称：説明`) に整形する (plansubmit-error-detail design 決定 #6)。
 *
 * 不明コードは mock fallback 「エラー(コード)」のみを返す。 `errorMessage`
 * (proto `PlanSubmissionReceptionError.error_message`) は常に空文字になる —
 * 受信確認メッセージ標準規格 (Ver.1.6 §5.3.2 表 5-3) にはエラーメッセージ
 * 文字列に相当するデータ要素が存在しないため
 * (plan-submit-reject-bp-error-code design 決定 #6)。
 */
export function formatReceptionError(err: {
  errorCode: string
  errorMessage: string
}): string {
  const known = SUBMISSION_ERROR_CODES[err.errorCode]
  if (known !== undefined) {
    return `[${err.errorCode}] ${known.name}：${known.description}`
  }
  return `エラー(${err.errorCode})`
}

/**
 * 計画提出エラー分類 → 対処文の i18n key (plansubmit-error-detail design 決定 #3/#5)。
 * 分類そのもの (last_error → category) は backend の valueobject
 * (ClassifyPlanSubmitErrorCategory) が担い、 frontend はここで category → 表示
 * ラベルの写像のみ行う (bidding SEND_ERROR_CATEGORY_LABEL_KEY と同型)。 パラメータ (エリア・BG 等) は載せない
 * 汎用対処文 (決定 #5)。
 */
export const ERROR_CATEGORY_LABEL_KEY: Record<
  ProcurementPlanSubmitErrorCategory,
  string
> = {
  [ProcurementPlanSubmitErrorCategory.UNSPECIFIED]:
    'page:planSubmission.errorCategory.unspecified',
  [ProcurementPlanSubmitErrorCategory.PLAN_MASTER_MISSING]:
    'page:planSubmission.errorCategory.planMasterMissing',
  [ProcurementPlanSubmitErrorCategory.DEMAND_FORECAST_UNCONFIRMED]:
    'page:planSubmission.errorCategory.demandForecastUnconfirmed',
  [ProcurementPlanSubmitErrorCategory.SOURCE_DATA_INCOMPLETE]:
    'page:planSubmission.errorCategory.sourceDataIncomplete',
  [ProcurementPlanSubmitErrorCategory.CREDENTIAL_NOT_CONFIGURED]:
    'page:planSubmission.errorCategory.credentialNotConfigured',
  [ProcurementPlanSubmitErrorCategory.RETRY_EXHAUSTED]:
    'page:planSubmission.errorCategory.retryExhausted',
  [ProcurementPlanSubmitErrorCategory.OTHER]:
    'page:planSubmission.errorCategory.other',
  [ProcurementPlanSubmitErrorCategory.GATE_CLOSED_ALL_KOMA]:
    'page:planSubmission.errorCategory.gateClosedAllKoma',
  [ProcurementPlanSubmitErrorCategory.CREDENTIAL_INVALID]:
    'page:planSubmission.errorCategory.credentialInvalid',
  [ProcurementPlanSubmitErrorCategory.OCCTO_UNREACHABLE]:
    'page:planSubmission.errorCategory.occtoUnreachable',
  [ProcurementPlanSubmitErrorCategory.OCCTO_CALL_LIMIT_REACHED]:
    'page:planSubmission.errorCategory.occtoCallLimitReached',
  [ProcurementPlanSubmitErrorCategory.BREAKDOWN_LIMIT_EXCEEDED]:
    'page:planSubmission.errorCategory.breakdownLimitExceeded',
}

/**
 * ステータス横のインライン表示専用の対処文 i18n key
 * (2026-09-17 Des 判断、issue #8283)。
 *
 * tooltip 用の [[ERROR_CATEGORY_LABEL_KEY]] は「状態説明。→ 次アクション。」の
 * 構造で 40〜50 文字あり、 一覧のステータス列 (220px・`size="xs"` = 約 18 文字)
 * では**状態説明の途中で切れて次アクションが読めない**。 AC-PLC-033-1 が
 * 求めるのは「hover せずに次アクションが分かる」ことなので、 インラインには
 * **次アクションだけ**を出し、 状態説明を含む全文は tooltip に残す。
 *
 * 列幅を広げる案は採らない — 需要調達計画一覧は 1440 幅で既に 420px 不足して
 * おり、 50 文字 (約 600px) を入れると不足が倍増する (実測 2026-09-17)。
 * 2 行 clamp も 36 文字にしかならず 50 文字に届かない。
 *
 * `GATE_CLOSED_ALL_KOMA` だけは次アクションが存在しない (提出できない) ので、
 * その事実を出す。
 */
export const ERROR_CATEGORY_INLINE_KEY: Record<
  ProcurementPlanSubmitErrorCategory,
  string
> = {
  [ProcurementPlanSubmitErrorCategory.UNSPECIFIED]:
    'page:planSubmission.errorCategory.unspecified',
  [ProcurementPlanSubmitErrorCategory.PLAN_MASTER_MISSING]:
    'page:planSubmission.errorCategory.planMasterMissingInline',
  [ProcurementPlanSubmitErrorCategory.DEMAND_FORECAST_UNCONFIRMED]:
    'page:planSubmission.errorCategory.demandForecastUnconfirmedInline',
  [ProcurementPlanSubmitErrorCategory.SOURCE_DATA_INCOMPLETE]:
    'page:planSubmission.errorCategory.sourceDataIncompleteInline',
  [ProcurementPlanSubmitErrorCategory.CREDENTIAL_NOT_CONFIGURED]:
    'page:planSubmission.errorCategory.credentialNotConfiguredInline',
  [ProcurementPlanSubmitErrorCategory.CREDENTIAL_INVALID]:
    'page:planSubmission.errorCategory.credentialInvalidInline',
  [ProcurementPlanSubmitErrorCategory.RETRY_EXHAUSTED]:
    'page:planSubmission.errorCategory.retryExhaustedInline',
  [ProcurementPlanSubmitErrorCategory.OTHER]:
    'page:planSubmission.errorCategory.otherInline',
  [ProcurementPlanSubmitErrorCategory.GATE_CLOSED_ALL_KOMA]:
    'page:planSubmission.errorCategory.gateClosedAllKomaInline',
  [ProcurementPlanSubmitErrorCategory.OCCTO_UNREACHABLE]:
    'page:planSubmission.errorCategory.occtoUnreachableInline',
  [ProcurementPlanSubmitErrorCategory.OCCTO_CALL_LIMIT_REACHED]:
    'page:planSubmission.errorCategory.occtoCallLimitReachedInline',
  // 上限値を含む 1 文で、tooltip 用と同じ文言 (短い次アクションが無い)
  [ProcurementPlanSubmitErrorCategory.BREAKDOWN_LIMIT_EXCEEDED]:
    'page:planSubmission.errorCategory.breakdownLimitExceeded',
}

/**
 * 汎用対処文 (conventions.md §サーバーエラー / AC-PLC-050 / AC-PLC-020-2 準拠) の
 * i18n key を variant 別に写像する (plan-submit-reject-bp-error-code design
 * 決定 #8)。BP コードも category も取得できない警告行の最終フォールバックに使う。
 *
 * 3 系統に分かれる理由: 差戻し (BP フォーマット不備が原因なら再試行で解消しない
 * ため管理者問い合わせを促す) と、送信失敗 (AC-PLC-050/020-2 が「元データ・
 * マスタの修正が不要」と伝える文言を必須とする) は、作成失敗・`submitFailed`
 * (SUBMIT_FAILED) と同じ「再作成・再提出してください」文言を出すと業務担当者の
 * 初動を誤らせる。
 * `inconsistent` / `approvalPending` (PlanSubmitErrorIndicator の対象外の
 * hasWarning variant) は対象外のため既存の汎用文言のまま据え置く。
 */
export const ERROR_INDICATOR_FALLBACK_KEY: Record<StatusBadgeVariant, string> =
  {
    unsubmitted: 'page:planSubmission.errorIndicator.detailUnavailable',
    generating: 'page:planSubmission.errorIndicator.detailUnavailable',
    creationFailed:
      'page:planSubmission.errorIndicator.fallback.creationOrSubmitFailed',
    rejected: 'page:planSubmission.errorIndicator.fallback.rejected',
    submitted: 'page:planSubmission.errorIndicator.detailUnavailable',
    accepted: 'page:planSubmission.errorIndicator.detailUnavailable',
    inconsistent: 'page:planSubmission.errorIndicator.detailUnavailable',
    submitting: 'page:planSubmission.errorIndicator.detailUnavailable',
    approvalPending: 'page:planSubmission.errorIndicator.detailUnavailable',
    submitFailed:
      'page:planSubmission.errorIndicator.fallback.creationOrSubmitFailed',
    sendFailed: 'page:planSubmission.errorIndicator.fallback.sendFailed',
  }

/**
 * インライン表示専用の汎用対処文 key ([[ERROR_CATEGORY_INLINE_KEY]] と同じ理由)。
 * tooltip 用の [[ERROR_INDICATOR_FALLBACK_KEY]] は 40〜50 文字あり、
 * 220px では次アクションまで届かない。`detailUnavailable` (差戻し以外の variant
 * で使う) はインライン表示されないので tooltip 用のまま。
 */
export const ERROR_INDICATOR_FALLBACK_INLINE_KEY: Record<
  StatusBadgeVariant,
  string
> = {
  unsubmitted: 'page:planSubmission.errorIndicator.detailUnavailable',
  generating: 'page:planSubmission.errorIndicator.detailUnavailable',
  creationFailed:
    'page:planSubmission.errorIndicator.fallback.creationOrSubmitFailedInline',
  rejected: 'page:planSubmission.errorIndicator.fallback.rejectedInline',
  submitted: 'page:planSubmission.errorIndicator.detailUnavailable',
  accepted: 'page:planSubmission.errorIndicator.detailUnavailable',
  inconsistent: 'page:planSubmission.errorIndicator.detailUnavailable',
  submitting: 'page:planSubmission.errorIndicator.detailUnavailable',
  approvalPending: 'page:planSubmission.errorIndicator.detailUnavailable',
  submitFailed:
    'page:planSubmission.errorIndicator.fallback.creationOrSubmitFailedInline',
  sendFailed: 'page:planSubmission.errorIndicator.fallback.sendFailedInline',
}

export type PlanRowErrorDetailInput = {
  errorCategory: ProcurementPlanSubmitErrorCategory
  receptionErrors: Array<{ errorCode: string; errorMessage: string }>
  /**
   * 内訳の件数の上限 (AC-PLC-064)。category が BREAKDOWN_LIMIT_EXCEEDED の時だけ
   * backend が set する。
   */
  breakdownRowLimit?: number
}

/**
 * category 行を `t()` で解決する `InterpolatedMessage` にする。`keys` は
 * label 用 ([[ERROR_CATEGORY_LABEL_KEY]]) か inline 用 ([[ERROR_CATEGORY_INLINE_KEY]])。
 * 上限値を持つ行は `{{limit}}` を載せる (整形は `translateInterpolatedMessage`)。
 */
export function categoryInterpolatedMessage(
  keys: Record<ProcurementPlanSubmitErrorCategory, string>,
  message: {
    category: ProcurementPlanSubmitErrorCategory
    breakdownRowLimit?: number
  },
): InterpolatedMessage {
  const key = keys[message.category]
  return message.breakdownRowLimit === undefined
    ? { key }
    : { key, limit: message.breakdownRowLimit }
}

/**
 * 受付エラー 1 件を常時インライン表示用の `[code] 名称` 形式に整形する
 * (plansubmit-error-detail design 決定 #1)。 `formatReceptionError` と異なり
 * 説明文 (`：` 以降) を含めない。 不明コードは `formatReceptionError` と同じ
 * `エラー(コード)` fallback に揃える (ERROR_MSG は tooltip/popover 側の全文でのみ
 * 補足するため、インライン要約には含めない)。
 */
function formatReceptionErrorCodeName(err: { errorCode: string }): string {
  const known = SUBMISSION_ERROR_CODES[err.errorCode]
  if (known !== undefined) {
    return `[${err.errorCode}] ${known.name}`
  }
  return `エラー(${err.errorCode})`
}

/**
 * 警告アイコン横の常時インライン表示の要約を導出する純関数
 * (plansubmit-error-detail design 決定 #1/#2/#4)。
 *
 * - 正常コード (IsOk、review fix #3) を除いた受付エラーが 1 件以上あれば、
 *   先頭 1 件を `[code] 名称` に整形して返し、残りの件数を `remainingCount` に
 *   載せる (決定 #4: 複数件は先頭 1 件 + 「他N件」をインライン表示、全件は
 *   `resolvePlanErrorMessages` 経由の tooltip/popover に表示する)。
 * - 受付エラーが無い (category fallback / 汎用 fallback) 場合は `undefined` を
 *   返す — インライン表示すべき「エラーコード + エラー名」が存在しないため
 *   (決定 #2: category ラベルはインライン化しない)。
 */
export function resolveInlineReceptionErrorSummary(
  receptionErrors: PlanRowErrorDetailInput['receptionErrors'],
): { text: string; remainingCount: number } | undefined {
  const errors = receptionErrors.filter(
    (e) => !isOkReceptionErrorCode(e.errorCode),
  )
  const [first, ...rest] = errors
  if (first === undefined) {
    return undefined
  }
  return {
    text: formatReceptionErrorCodeName(first),
    remainingCount: rest.length,
  }
}

/**
 * 警告アイコンの tooltip/popover に表示するエラー詳細メッセージ一覧を導出する
 * 純関数 (AC-PLC-008 / 033-1 / 033-2)。
 *
 * - receptionErrors から正常コード (IsOk: "00" / 空文字、review fix #3) を
 *   除外した上で 1 件以上残れば → 差戻し (REJECTED) の BP コード詳細を全件
 *   表示 (states: 複数件は全件表示)。
 * - 上記が空 かつ category が UNSPECIFIED でない → category の対処文 1 件
 *   (作成失敗・提出失敗・差戻しの stage-A fallback 経路)。
 * - どちらも無い → 空配列 (呼び出し元は正常 status ではアイコン自体を出さず、
 *   hasWarning 時〈= 正規の stage-B 差戻し等で detail が取得できなかった〉は
 *   汎用 fallback 文言を出す — 判定は呼び出し元の component が hasWarning と
 *   併せて行う、review fix #1)。
 *
 * BREAKDOWN_LIMIT_EXCEEDED は上限値が無ければ空配列 (呼び出し元は汎用 fallback に倒す)。
 *
 * i18n 解決 (category → 文言) は呼び出し元 (component) が t(ERROR_CATEGORY_LABEL_KEY[...])
 * で行う。 この関数は分類済みデータの整形のみを担う純関数のため、 category の
 * 場合は解決前の enum 値をそのまま返す。
 */
export function resolvePlanErrorMessages(input: PlanRowErrorDetailInput): Array<
  | { kind: 'receptionError'; text: string }
  | {
      kind: 'category'
      category: ProcurementPlanSubmitErrorCategory
      breakdownRowLimit?: number
    }
> {
  const errorReceptionErrors = input.receptionErrors.filter(
    (e) => !isOkReceptionErrorCode(e.errorCode),
  )
  if (errorReceptionErrors.length > 0) {
    return errorReceptionErrors.map((e) => ({
      kind: 'receptionError' as const,
      text: formatReceptionError(e),
    }))
  }
  if (input.errorCategory === ProcurementPlanSubmitErrorCategory.UNSPECIFIED) {
    return []
  }
  if (
    input.errorCategory ===
    ProcurementPlanSubmitErrorCategory.BREAKDOWN_LIMIT_EXCEEDED
  ) {
    const breakdownRowLimit = displayableBreakdownRowLimit(input)
    // 上限値が届かない時は文言を組めないので、汎用 fallback に倒す (空配列)
    return breakdownRowLimit === undefined
      ? []
      : [
          {
            kind: 'category' as const,
            category: input.errorCategory,
            breakdownRowLimit,
          },
        ]
  }
  return [{ kind: 'category' as const, category: input.errorCategory }]
}

/**
 * 内訳の件数の上限超過 (AC-PLC-064) の文言に入れる上限値。category が
 * BREAKDOWN_LIMIT_EXCEEDED で上限値が届いている時だけ返し、それ以外は undefined
 * (呼び出し元は上限超過の文言を出さず、汎用の作成失敗の表示に倒す)。
 * 一覧・カードの原因テキストと計画詳細の誘導メッセージが同じ判定を使う。
 */
export function displayableBreakdownRowLimit(input: {
  errorCategory: ProcurementPlanSubmitErrorCategory
  breakdownRowLimit?: number
}): number | undefined {
  return input.errorCategory ===
    ProcurementPlanSubmitErrorCategory.BREAKDOWN_LIMIT_EXCEEDED
    ? input.breakdownRowLimit
    : undefined
}

export type ResolvedPlanErrorRow =
  | {
      kind: 'messages'
      messages: ReturnType<typeof resolvePlanErrorMessages>
    }
  | { kind: 'fallback'; fallbackKey: string }

/**
 * `resolvePlanErrorMessages` が空を返す時に variant 別の汎用対処文 key
 * (`ERROR_INDICATOR_FALLBACK_KEY`) へ倒すところまでを 1 つにまとめた純関数。
 *
 * `PlanSubmitErrorIndicator` (一覧の警告アイコン) と計画タイプカードのヘッダー
 * エラー行 (`pages/SubmissionManagement/models/cardErrorRows.ts`) が同じ
 * 「messages が空なら fallback key」判定を個別に複製していたため、ここへ
 * 集約する (両呼び出し元とも i18n 解決は自分で `t()` する。この関数は解決前の
 * enum 値・key 文字列のみを返す)。
 */
export function resolvePlanErrorRow(
  input: PlanRowErrorDetailInput,
  variant: StatusBadgeVariant,
): ResolvedPlanErrorRow {
  const messages = resolvePlanErrorMessages(input)
  if (messages.length > 0) {
    return { kind: 'messages', messages }
  }
  return {
    kind: 'fallback',
    fallbackKey: ERROR_INDICATOR_FALLBACK_KEY[variant],
  }
}
